import { useState, useMemo } from 'react';

import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { fromApiBoolean, toApiBoolean } from '@liquidweb-dev/club-sauce';
import BuyNowPaymentMethod from 'components/standard/BuyNowPaymentMethod';
import ErrorState from 'components/standard/ErrorState';
import LWForm, { FormSubmitHandler } from 'components/LWForm/LWForm';
import useDomainDetails from 'modules/queries/network/dns/domain/useDetails';
import useDomainRenew from 'modules/queries/network/dns/domain/useRenew';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import DomainRenewalSummary from './DomainRenewalSummary';
import Wrapper, { type WrapperProps } from './Wrapper';

interface DrawerDomainRenewalProps extends WrapperProps {
	domain: string;
	onSuccess?: () => void;
}

const DrawerDomainRenewal = ({
	domain,
	onSuccess,
	onClose,
	...drawerProps
}: DrawerDomainRenewalProps) => {
	const initialValues = { numberOfYears: 1, privacy: false, autoRenew: false };
	const [formValues, setFormValues] = useState(initialValues);

	const {
		data: domainDetails,
		isLoading: isDomainDetailsLoading,
		isError: isDomainDetailsError,
		refetch: refetchDomainDetails,
	} = useDomainDetails(
		{
			domain,
			alsowith: ['renewal_max_years'],
		},
		{ enabled: Boolean(drawerProps.open && domain) },
	);

	const { data: productDetails, isLoading: isProductDetailsLoading } =
		useProductFlatDetails(
			{ product_code: 'DREG' },
			{ enabled: Boolean(drawerProps.open && domain) },
		);

	const { mutateAsync: renewDomain, isLoading: isRenewing } = useDomainRenew();

	const privacyPrice = useMemo(() => {
		if (!productDetails?.flat_options) {
			return null;
		}

		return (
			productDetails.flat_options
				.find((option) => option.key === 'DomainPrivacy')
				?.values.find((value) => value.value === 'On')?.region_id_prices?.[1] ??
			null
		);
	}, [productDetails]);

	const isExpired = fromApiBoolean(domainDetails?.is_expired);
	const renewalMaxYears = domainDetails?.renewal_max_years || 1;
	const renewalPrice = domainDetails?.renewal_price || 0;
	const isRGP = fromApiBoolean(domainDetails?.is_rgp);
	const redemptionPrice = domainDetails?.redemption_price ?? null;

	const isLoading = isDomainDetailsLoading || isProductDetailsLoading;

	const handleClose = () => {
		setFormValues(initialValues);
		onClose();
	};

	if (isLoading) {
		return (
			<Wrapper onClose={handleClose} {...drawerProps}>
				<DrawerContent>
					<Skeleton variant="rounded" height={400} width="100%" />
				</DrawerContent>
			</Wrapper>
		);
	}

	if (isDomainDetailsError || !domainDetails) {
		return (
			<Wrapper onClose={handleClose} {...drawerProps}>
				<DrawerContent sx={{ alignItems: 'center', justifyContent: 'center' }}>
					<ErrorState onTryAgain={refetchDomainDetails} />
				</DrawerContent>
			</Wrapper>
		);
	}

	const handleSubmit: FormSubmitHandler<typeof initialValues> = async (
		values,
	) => {
		try {
			await renewDomain({
				domain,
				duration: values.numberOfYears as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
				privacy: toApiBoolean(values.privacy),
				autoRenew: toApiBoolean(values.autoRenew),
			});

			handleClose();
			onSuccess?.();
		} catch (_error) {
			// Error handling is done in the mutation
		}
	};

	const renewalTotal = Number(renewalPrice) * formValues.numberOfYears;

	const privacyTotal =
		formValues.privacy && privacyPrice ? Number(privacyPrice) : 0;
	const redemptionTotal =
		isRGP && redemptionPrice ? Number(redemptionPrice) : 0;
	const grandTotal = renewalTotal + privacyTotal + redemptionTotal;

	return (
		<Wrapper onClose={handleClose} {...drawerProps}>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				onSubmit={handleSubmit}
				style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
			>
				<DrawerContent>
					<DomainRenewalSummary
						domain={domain}
						renewalPrice={renewalPrice}
						privacyPrice={privacyPrice}
						privacyEnabled={formValues.privacy}
						isRGP={isRGP}
						isExpired={isExpired}
						redemptionPrice={redemptionPrice}
						renewalMaxYears={renewalMaxYears}
					/>
				</DrawerContent>

				<DrawerFooter sx={{ gap: 200 }}>
					<Stack direction="row" gap={200} justifyContent="space-between">
						<Typography variant="body-md">Due today</Typography>
						<Typography variant="body-md" fontWeight={700}>
							{formatCurrency(grandTotal)}
						</Typography>
					</Stack>
					<BuyNowPaymentMethod />

					<HookedSubmit
						fullWidth
						variant="primary"
						color="highlight"
						loading={isRenewing}
						size="large"
					>
						Renew
					</HookedSubmit>
				</DrawerFooter>
			</LWForm>
		</Wrapper>
	);
};

export default DrawerDomainRenewal;
